import React, { useEffect, useRef, useState } from 'react';
import './style.css';
import LeftSideBar from '../../layouts/LeftSideBar';
import RightSideBar from '../../layouts/RightSideBar';
import ChatWrapper from '../../layouts/Main';
import Header from '../../layouts/Header';
import { Button, Form, notification } from 'antd';
import CustomButtonGroup from '../../baseComponents/customButtonGroup';
import customiseIcon from '../../../assets/customiseIcon.svg';
import cometchatDemoLogo from '../../../assets/cometchatLogoDemo.svg';
import cometchatLogo from '../../../assets/cometchatLogo.svg';
import CustomDrawer from '../../baseComponents/CustomDrawer';
import HeaderBanner from '../../layouts/HeaderBanner';
import builderConstants from '../../../builderConstants';
import { CometChatSettingsInterface } from '../../../sampleApp/src/CometChatSettings';
import { useCometChatContext } from '../../../sampleApp/src/context/CometChatContext';

/**
 * Props for the Builder component.
 */
interface BuilderProps {
  /** Indicates if the user is authenticated. */
  isAuthenticated?: boolean;
  /** Callback function triggered when values change. */
  onValuesChanged?: (value?: CometChatSettingsInterface) => void;
  /** Authenticated builder settings. */
  authBuilderSetting?: CometChatSettingsInterface;
  /** Callback function for modal confirmation. */
  modalCallbackOnOk?: () => void;
  /** Callback function for dashboard click. */
  onDashboardClick?: () => void;
  /** Callback function triggered on download. */
  onDownloadCallBack?: (input: string) => void;
  /** Callback function triggered on reset click. */
  onResetClick?: () => void;
}

/**
 * Builder component for customizing chat UI settings.
 *
 * @param {BuilderProps} props - The component props.
 * @returns {JSX.Element} The rendered Builder component.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function Builder({
  isAuthenticated = false,
  onValuesChanged,
  authBuilderSetting,
  modalCallbackOnOk,
  onDashboardClick,
  onDownloadCallBack,
  onResetClick,
}: BuilderProps) {
  const [form] = Form.useForm();
  const [isFullScreen, setFullScreen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(
    window.innerHeight < window.screen.height / 2 || window.innerWidth < 769
  );
  const [isDataSaved, setIsDataSaved] = useState(true);
  const [activeComponent, setActiveComponent] = useState('layout');
  const [isDemoVisible, setIsDemoVisible] = useState(true);
  const [previousValues, setPreviousValues] = useState<CometChatSettingsInterface>();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { chatFeatures, callFeatures, styleFeatures, layoutFeatures } = useCometChatContext();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerHeight < window.screen.height / 2 || window.innerWidth < 769) {
        if (window.devicePixelRatio <= 1.5 && window.innerWidth > 769 && window.innerHeight > 500) {
          setIsMobileDevice(false);
        } else {
          setIsMobileDevice(true);
        }
      } else {
        setIsMobileDevice(false);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Syncs form values with styleFeatures when they change.
   */
  useEffect(() => {
    const currentValues = form.getFieldsValue();

    form.setFieldsValue({
      ...currentValues,
      style: {
        ...currentValues.style,
        ...styleFeatures,
      },
    });
  }, [styleFeatures, form]);

  /**
   * Handles full-screen transition after authentication.
   */
  useEffect(() => {
    if (isAuthenticated) {
      const intervalId = setInterval(() => {
        setFullScreen(false);
        clearInterval(intervalId);
      }, 1500);

      return () => clearInterval(intervalId);
    }
  }, [isAuthenticated]);

  /**
   * Watches for changes in form values and triggers onValuesChanged callback.
   */
  useEffect(() => {
    if (isAuthenticated) {
      setPreviousValues(form.getFieldsValue(true));
      intervalRef.current = setInterval(() => {
        const currentValues = form.getFieldsValue(true);

        if (JSON.stringify(currentValues) !== JSON.stringify(previousValues)) {
          if (onValuesChanged) {
            onValuesChanged(currentValues);
          }
          setIsDataSaved(false);
          setPreviousValues(currentValues);
        } else {
          setIsDataSaved(true);
        }
      }, 5000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, form, previousValues, onValuesChanged]);

  /**
   * Handles file download and triggers callback.
   */
  const handleDownload = () => {
    const data = form.getFieldsValue(true);
    localStorage.setItem('ccBuilderConfig', JSON.stringify(data));

    if (isAuthenticated) {
      try {
        onDownloadCallBack?.(data);
      } catch (error) {
        console.error('downloadCallBack error', error);
      }
    } else {
      redirectToApp(data);
    }
  };

  /**
   * Redirects to the application(dashboard) with saved builder settings.
   */
  const redirectToApp = (value: string) => {
    const targetOrigin = builderConstants.dashboardDomain;
    const newTab = window.open(`${targetOrigin}/apps`, '_blank');
    if (!newTab) {
      return;
    }

    const readyHandler = (event: MessageEvent) => {
      if (event.origin === targetOrigin && event.data.type === 'READY') {
        try {
          newTab.postMessage({ type: 'SET', key: 'ccBuilderConfig', value }, targetOrigin);
        } catch (err) {
          console.error('Failed to send message:', err);
        }

        window.removeEventListener('message', readyHandler);

        const confirmationHandler = (event: MessageEvent) => {
          if (event.origin === targetOrigin && event.data.type === 'SET_SUCCESS') {
            window.removeEventListener('message', confirmationHandler);
          } else {
            console.warn('Received an unexpected message from:', event.origin);
          }
        };

        window.addEventListener('message', confirmationHandler);
      }
    };

    window.addEventListener('message', readyHandler);
  };

  /**
   * Toggles full-screen mode.
   */
  const handleFullScreen = () => {
    setFullScreen((prev) => !prev);
  };

  /**
   * Copies the current URL to clipboard.
   */
  const handleShare = () => {
    const currentUrl = window.location.href;
    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        notification.success({
          message: '',
          description: 'Copied',
          duration: 1,
          closeIcon: null,
        });
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  const getButtonView = (
    <>
      <CustomButtonGroup>
        <Button
          style={{
            background: activeComponent === 'layout' ? 'white' : 'transparent',
          }}
          className="buttonStyle"
          onClick={() => {
            setActiveComponent('layout');
            setIsMobile(false);
          }}
          icon={
            <div
              className="ButtonIcon desktopIcon"
              style={{
                background: activeComponent === 'layout' ? '#6852D6' : '',
              }}
            />
          }
        />
        <Button
          style={{
            background: activeComponent === 'chat' ? '#FFF' : 'transparent',
          }}
          className="buttonStyle"
          onClick={() => {
            setActiveComponent('chat');
            setIsMobile(true);
          }}
          icon={
            <div
              className="ButtonIcon mobileIcon"
              style={{
                background: activeComponent === 'chat' ? '#6852D6' : '#A1A1A1',
              }}
            />
          }
        />
      </CustomButtonGroup>
    </>
  );

  /**
   * Handles the click event for the "Customize" button.
   * Hides the demo view and exits full-screen mode.
   */
  const handleCustomiseClick = () => {
    setFullScreen(false);
    setIsDemoVisible(false);
  };

  /**
   * Handles the close event for the drawer.
   * Exits full-screen mode when closed.
   */
  const onClose = () => {
    setFullScreen(false);
  };

  /**
   * Updates the form values when authentication state or builder settings change.
   * Runs whenever `authBuilderSetting`, `form`, or `isAuthenticated` changes.
   */
  useEffect(() => {
    if (isAuthenticated !== undefined && authBuilderSetting) {
      form.setFieldsValue(authBuilderSetting);
    }
  }, [authBuilderSetting, form, isAuthenticated]);

  function isTrueDesktop() {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const isMobileOrTablet = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini|Tablet/i.test(ua);
    return !(isMobileOrTablet || isTouchDevice);
  }

  return (
    <div id="builderApp" className="builderApp">
      {!isTrueDesktop() || isMobileDevice ? (
        <div className="builderMobileView">
          <div className="builderMobileView__icon" />
          <div className="builderMobileView__title">{'Your screen is too small'}</div>
          <div className="builderMobileView__subtitle">{'Please access CometChat Builder from a desktop device.'}</div>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={
            authBuilderSetting
              ? authBuilderSetting
              : { chatFeatures: chatFeatures, callFeatures: callFeatures, style: styleFeatures, layout: layoutFeatures }
          }
          style={{ height: '100%', width: '100%' }}
        >
          <div className="chatApp">
            <div style={{ width: '100%' }}>
              {!isAuthenticated && <HeaderBanner />}
              <Header
                textContent={!isDemoVisible || isAuthenticated ? 'Export Code' : 'Customize'}
                handleFullScreen={handleFullScreen}
                handleShare={handleShare}
                onSave={!isDemoVisible || isAuthenticated ? handleDownload : handleCustomiseClick}
                logo={!isDemoVisible || isAuthenticated ? cometchatLogo : cometchatDemoLogo}
                isPreviewScreen={isDemoVisible}
                isAuthenticated={isAuthenticated}
                onDashboardClick={onDashboardClick}
                isDataSaved={isDataSaved}
                onResetClick={onResetClick}
              />
            </div>

            <div className="appContent" style={{ overflow: isDemoVisible ? 'auto' : 'hidden' }}>
              <CustomDrawer
                open={!isFullScreen}
                onClose={onClose}
                placement="left"
                mask={false}
                closeIcon={false}
                width="fit-content"
                style={{
                  marginTop: isAuthenticated ? '51px' : '83px',
                  maxWidth: '283px',
                  minWidth: '283px',
                  borderRight: '1px solid #F5F5F5',
                }}
              >
                <LeftSideBar form={form} />
              </CustomDrawer>
              <div
                className="mainContent"
                style={{
                  width: isFullScreen ? '70%' : '60%',
                  height: isDemoVisible && isFullScreen && !isAuthenticated ? '97%' : '100%',
                  transition: 'width 0.9s ease, height 0.9s ease, transform 0.9s ease',
                }}
              >
                {getButtonView}
                <ChatWrapper
                  isMobile={isMobile}
                  chatWrapperHeight={
                    isDemoVisible && isFullScreen && !isAuthenticated ? `calc(100vh - 250px)` : 'calc(100vh - 185px)'
                  }
                  isFullScreen={isFullScreen}
                />

                {isDemoVisible && isFullScreen && !isAuthenticated && (
                  <button className="customiseButton" onClick={handleCustomiseClick}>
                    <span>Customize </span>
                    <span>
                      <img src={customiseIcon} alt="" />
                    </span>
                  </button>
                )}
              </div>

              <CustomDrawer
                open={!isFullScreen}
                onClose={onClose}
                placement="right"
                headerStyle={{ display: 'none' }}
                mask={false}
                closeIcon={false}
                width="fit-content"
                style={{
                  marginTop: isAuthenticated ? '51px' : '83px',
                  maxWidth: '282px',
                  minWidth: '282px',
                  borderLeft: '1px solid #F5F5F5',
                }}
              >
                <RightSideBar form={form} />
              </CustomDrawer>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
}

export default React.memo(Builder);
